import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { PullQuote } from '@/components/common/PullQuote';
import { WistiaSection } from '@/components/common/WistiaComponents/WistiaSection';
import { Col } from '@/components/layout/Grid';
import { CardGrid } from '@/components/page/courses/CardGrid';
import { DataChips } from '@/components/page/courses/DataChips/DataChips';
import { DeliveryModel } from '@/components/page/courses/DeliveryModel';
import { IconAndTitleList } from '@/components/page/courses/IconAndTitleList/IconAndTitleList';
import { NestedAccordion } from '@/components/page/courses/nestedAccordion/NestedAccordion';
import { Overview } from '@/components/page/courses/Overview';
import { PartnerLogos } from '@/components/page/courses/PartnerLogos';

import type {
  BlockWithAudience,
  ProgrammeB2BType,
  ProgrammeB2CType,
} from '@/types/shared';

interface ProgrammePageLayoutProps extends BlockWithAudience {
  programmeData: ProgrammeB2CType | ProgrammeB2BType;
}

const b2cOrder = [
  { name: 'overview', gap: '--space-block-lg' },
  { name: 'video', gap: '--space-page-sm' },
  { name: 'courseSummary', gap: '--space-block-lg' },
  { name: 'skills', gap: '--space-block-lg' },
  { name: 'partnerLogos', gap: '--space-block-lg' },
  { name: 'curriculumAccordion', gap: '--space-block-xl' },
  { name: 'cardGridTop', gap: '--space-block-xl' },
  { name: 'pullQuote', gap: '--space-block-lg' },
  { name: 'deliveryModel', gap: '--space-block-xl' },
  { name: 'cardGridBottom', gap: '--space-block-xl' },
] as const;

const b2bOrder = [
  { name: 'overview', gap: '--space-block-lg' },
  { name: 'video', gap: '--space-page-sm' },
  { name: 'skills', gap: '--space-block-md' },
  { name: 'courseSummary', gap: '--space-block-xl' },
  { name: 'partnerLogos', gap: '--space-block-lg' },
  { name: 'curriculumAccordion', gap: '--space-block-xl' },
  { name: 'deliveryModel', gap: '--space-block-xl' },
  { name: 'cardGrid', gap: '--space-block-xl' },
  { name: 'pullQuote', gap: '--space-block-lg' },
] as const;

type OrderUnion = (typeof b2cOrder)[number] | (typeof b2bOrder)[number];

export const ProgrammePageLayout = ({
  programmeData,
  pageAudience,
  isAudienceSwitcherEnabled,
}: ProgrammePageLayoutProps) => {
  const pageOrder =
    programmeData._type === 'programmeB2C' ? b2cOrder : b2bOrder;

  return pageOrder.map((item: OrderUnion) => {
    if (
      !programmeData[item.name] &&
      !programmeData?.linkedProgramme?.[item.name]
    )
      return null;

    const pickData = (propertyName) => {
      return (
        programmeData[item.name]?.[propertyName] ||
        programmeData?.linkedProgramme?.[item.name]?.[propertyName]
      );
    };

    switch (item.name) {
      case 'overview': {
        return (
          <Spacer gap={item.gap} key={item.name}>
            <Overview
              heading={pickData('heading')}
              body={pickData('body')}
              pageAudience={pageAudience}
              isAudienceSwitcherEnabled={isAudienceSwitcherEnabled}
            />
          </Spacer>
        );
      }
      case 'courseSummary': {
        return (
          <Spacer gap={item.gap} key={item.name}>
            <IconAndTitleList
              heading={pickData('heading')}
              list={pickData('list')}
            />
          </Spacer>
        );
      }
      case 'partnerLogos': {
        return (
          <Spacer gap={item.gap} key={item.name}>
            <PartnerLogos
              heading={pickData('heading')}
              logos={pickData('logos')}
            />
          </Spacer>
        );
      }
      case 'skills': {
        return (
          <Spacer gap={item.gap} key={item.name}>
            <DataChips
              heading={pickData('heading')}
              badges={pickData('badges')}
            />
          </Spacer>
        );
      }
      case 'curriculumAccordion': {
        return (
          <Spacer gap={item.gap} key={item.name}>
            <NestedAccordion
              heading={pickData('heading')}
              outerAccordion={pickData('outerAccordion')}
              pageAudience={pageAudience}
              isAudienceSwitcherEnabled={isAudienceSwitcherEnabled}
            />
          </Spacer>
        );
      }
      case 'cardGrid':
      case 'cardGridTop':
      case 'cardGridBottom': {
        return (
          <Spacer gap={item.gap} key={item.name}>
            <CardGrid heading={pickData('heading')} cards={pickData('cards')} />
          </Spacer>
        );
      }
      case 'deliveryModel': {
        return (
          <Spacer gap={item.gap} key={item.name}>
            <DeliveryModel
              heading={pickData('heading')}
              footer={pickData('footer')}
              sections={pickData('sections')}
            />
          </Spacer>
        );
      }
      case 'pullQuote': {
        return (
          <PullQuoteWrapper>
            <Col
              sizes={{ base: 4, sm: 9, md: 5 }}
              start={{ base: 1, sm: 2, md: 1 }}
            >
              <Spacer gap={item.gap} key={item.name}>
                <PullQuote
                  CTA={pickData('CTA') as any}
                  authorDetails={pickData('authorDetails')}
                  decorationColour={pickData('decorationColour')}
                  layoutType={pickData('layoutType')}
                  logo={pickData('logo')}
                  quote={pickData('quote')}
                  surfaceColour={pickData('surfaceColour')}
                  isAudienceSwitcherEnabled={isAudienceSwitcherEnabled}
                  pageAudience={pageAudience}
                />
              </Spacer>
            </Col>
          </PullQuoteWrapper>
        );
      }
      case 'video': {
        return (
          <Spacer gap={item.gap} key={item.name}>
            <VideoWrapper>
              <WistiaSection
                imageThumbnail={pickData('imageThumbnail')}
                videoId={pickData('videoId')}
                trackingTag={pickData('trackingTag')}
                videoLength={pickData('videoLength')}
                videoTitle={pickData('videoTitle')}
              />
            </VideoWrapper>
          </Spacer>
        );
      }

      default:
        return null;
    }
  });
};

const Spacer = ({
  gap,
  children,
}: {
  gap: string;
  children: React.ReactNode;
}) => {
  return <div style={{ marginTop: `var(${gap})` }}>{children}</div>;
};

const PullQuoteWrapper = styled.div`
  display: grid;
  gap: var(--space-component-xl);

  grid-template-columns: repeat(4, 1fr);
  ${screen.sm} {
    grid-template-columns: repeat(12, 1fr);
  }
  ${screen.md} {
    grid-template-columns: repeat(6, 2fr);
  }
`;

const VideoWrapper = styled.div`
  padding-top: var(--space-component-lg);
`;
